import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';

import { FAQ_LIST, parseFaqInlineMarkdownLinks } from '@/landing/e-commerce/config';
import {
  aiRemovalURL,
  aiVideoEditURL,
  faceSwapperURL,
  imageEnhanceURL,
  videoTranslateURL,
  voiceCloneURL,
} from '@/utils/routeConstants';

import {
  ASSET_BASE,
  AVATAR_SHOWCASE_LEFT_VIDEOS,
  AVATAR_SHOWCASE_RIGHT_VIDEOS,
  customerLogos,
  featureBlocks,
  HOME_CTA_URL,
  HOME_TEMPLATE_GRID_PAGE_SIZE,
  stats,
  testimonials,
  toolTags,
} from './config';
import styles from './index.module.scss';
import { ArrowIcon, HeroMarqueeVideoCard, RevealSection, TemplateCard } from './shared';
import { useSharedTemplateList } from './template-list-context';

export const LogosSection = () => (
  <RevealSection className={styles.logoSection}>
    <p>Trusted by 2M+ creators and brands worldwide</p>
    <div className={styles.logoMask}>
      <div className={styles.logoTrack}>
        {[...customerLogos, ...customerLogos].map((logo, index) => (
          <div className={styles.logoItem} key={`${logo.id}-${index}`}>
            <Image src={logo.src} alt={logo.alt} width={150} height={60} />
          </div>
        ))}
      </div>
    </div>
  </RevealSection>
);

const steps = [
  {
    index: '01',
    title: 'Choose a template',
    description:
      'Quickly pick a template that fits your niche and style, then start with a proven structure',
    image: `${ASSET_BASE}/step-1.png`,
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M21.64 3.6398L20.36 2.3598C20.2475 2.24612 20.1136 2.15587 19.966 2.09428C19.8184 2.03269 19.66 2.00098 19.5 2.00098C19.3401 2.00098 19.1817 2.03269 19.0341 2.09428C18.8865 2.15587 18.7526 2.24612 18.64 2.3598L2.36004 18.6398C2.24636 18.7523 2.15612 18.8863 2.09452 19.0339C2.03293 19.1815 2.00122 19.3399 2.00122 19.4998C2.00122 19.6598 2.03293 19.8181 2.09452 19.9657C2.15612 20.1133 2.24636 20.2473 2.36004 20.3598L3.64005 21.6398C3.75187 21.7547 3.88558 21.846 4.03329 21.9084C4.181 21.9708 4.33971 22.0029 4.50005 22.0029C4.66038 22.0029 4.81909 21.9708 4.9668 21.9084C5.11451 21.846 5.24822 21.7547 5.36005 21.6398L21.64 5.3598C21.755 5.24798 21.8463 5.11427 21.9087 4.96656C21.971 4.81885 22.0031 4.66014 22.0031 4.4998C22.0031 4.33947 21.971 4.18076 21.9087 4.03305C21.8463 3.88534 21.755 3.75162 21.64 3.6398Z"
          stroke="white"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M14 7L17 10"
          stroke="white"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M5 6V10"
          stroke="white"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M19 14V18"
          stroke="white"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M10 2V4"
          stroke="white"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M7 8H3"
          stroke="white"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M21 16H17"
          stroke="white"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M11 3H9"
          stroke="white"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    ),
  },
  {
    index: '02',
    title: 'Replace your assets',
    description:
      'Upload your images, clips, and copy, then auto-place them into key scenes with smart layout and aspect-ratio fitting.',
    image: `${ASSET_BASE}/step-2.png`,
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
          stroke="white"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M17 8L12 3L7 8"
          stroke="white"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M12 3V15"
          stroke="white"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    ),
  },
  {
    index: '03',
    title: 'Download video',
    description:
      'Generate a high-quality final video in one click, export in multiple sizes, and download it for ads, social, or your website.',
    image: `${ASSET_BASE}/step-3.png`,
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
          stroke="white"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M7 10L12 15L17 10"
          stroke="white"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M12 15V3"
          stroke="white"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    ),
  },
];

const toolCards = [
  {
    id: 'translation',
    title: 'AI Translation',
    description:
      'Translate videos into multiple languages with synced subtitles, dubbing, and lip-sync for global-ready content',
    href: videoTranslateURL,
    style: {
      background: 'linear-gradient(135deg, #2B7FFF 0%, #00B8DB 100%)',
    },
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
          stroke="white"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M12 2C9.43223 4.69615 8 8.27674 8 12C8 15.7233 9.43223 19.3038 12 22C14.5678 19.3038 16 15.7233 16 12C16 8.27674 14.5678 4.69615 12 2Z"
          stroke="white"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M2 12H22"
          stroke="white"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    ),
  },
  {
    id: 'removal',
    title: 'AI Removal',
    description:
      'Remove backgrounds, unwanted objects, distractions, or watermarks in one click for cleaner, more focused visuals',
    href: aiRemovalURL,
    style: {
      background: 'linear-gradient(135deg, #FE9A00 0%, #FF6900 100%)',
    },
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
          stroke="white"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M12 18C15.3137 18 18 15.3137 18 12C18 8.68629 15.3137 6 12 6C8.68629 6 6 8.68629 6 12C6 15.3137 8.68629 18 12 18Z"
          stroke="white"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z"
          stroke="white"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    ),
  },
  {
    id: 'face-swap',
    title: 'Face Swap',
    description:
      'Swap faces in videos seamlessly for creative campaigns, character-based content, and personalized visuals',
    href: faceSwapperURL,
    style: {
      background: 'linear-gradient(135deg, #F6339A 0%, #FF2056 100%)',
    },
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M13.2 1.20008C15.4012 1.20008 17.1986 1.80265 18.6206 2.76779C18.7961 2.88691 18.9171 3.07087 18.9569 3.27919C18.9968 3.48752 18.9523 3.70315 18.8331 3.87865C18.714 4.05414 18.5301 4.17513 18.3217 4.215C18.1134 4.25486 17.8978 4.21034 17.7223 4.09122C16.5772 3.31465 15.0943 2.80122 13.2 2.80122C12.4577 2.80122 11.7857 2.86122 11.1832 2.9795C10.7186 5.27236 10.7846 6.94979 11.2517 8.09493L11.3092 8.21836C11.4574 8.49865 11.7051 8.73522 12.0643 8.95808C12.4877 9.22122 12.9917 9.42093 13.56 9.6515C14.0871 9.86408 14.7112 10.1178 15.1937 10.4821C15.7166 10.8764 16.1417 11.4506 16.1417 12.2786C16.1417 13.6526 15.3343 14.4661 14.7943 15.0001C14.4969 15.2932 14.3186 15.4655 14.2063 15.6404C14.1231 15.7689 14.112 15.8444 14.1343 15.9472C14.2543 16.4958 14.4729 17.1704 14.7977 17.9752C17.8337 17.3666 20.136 15.0772 20.9151 12.6164C20.9791 12.414 21.1209 12.2454 21.3092 12.1476C21.4975 12.0498 21.717 12.0308 21.9193 12.0948C22.1216 12.1588 22.2902 12.3005 22.388 12.4888C22.4859 12.6772 22.5049 12.8966 22.4409 13.0989C21.5357 15.9566 18.9934 18.5589 15.6214 19.4272C15.6429 19.4838 15.6729 19.5524 15.7123 19.6329C15.9334 20.0726 16.3757 20.6795 17.0863 21.4629C17.2202 21.6214 17.2874 21.8257 17.2737 22.0328C17.26 22.2398 17.1664 22.4334 17.0126 22.5728C16.8589 22.7122 16.6571 22.7864 16.4497 22.7799C16.2423 22.7734 16.0455 22.6866 15.9009 22.5378C15.2126 21.7784 14.6863 21.0935 14.3589 20.4926C14.1971 20.4132 14.0672 20.2811 13.9903 20.1181C13.2763 18.5966 12.7937 17.3126 12.5709 16.2858C12.4397 15.6824 12.6017 15.1784 12.8623 14.7738C13.0937 14.4146 13.4314 14.0966 13.6697 13.8618C14.2003 13.3389 14.5414 12.9386 14.5414 12.2786C14.5414 12.0892 14.4746 11.9426 14.2303 11.7592C13.9474 11.5449 13.53 11.3649 12.9617 11.1344C12.4337 10.9218 11.7857 10.6689 11.2209 10.3175C10.6423 9.95836 10.08 9.45608 9.77058 8.70008C9.21172 7.33122 9.13458 5.59722 9.45344 3.55722C9.17359 3.69835 8.90758 3.86536 8.65886 4.05608C7.62515 4.85493 6.92401 6.07979 6.61715 7.82922C6.5956 7.95133 6.54598 8.06676 6.4722 8.16642C6.39842 8.26608 6.30251 8.34724 6.19201 8.4035C5.29057 8.85577 4.39894 9.32731 3.51772 9.81779C3.33772 9.91979 3.18429 10.0098 3.05572 10.0886C3.10886 10.1504 3.16715 10.2164 3.22372 10.2866C3.59915 10.7589 4.17344 11.6341 3.74401 12.7226C3.47572 13.4041 3.19029 14.1086 3.06172 14.7866C2.93315 15.4638 2.99486 15.9549 3.24344 16.3021C3.32915 16.4221 3.46972 16.5258 3.73715 16.6141C4.02001 16.7066 4.36886 16.7589 4.82229 16.8181C5.64944 16.9269 6.85201 17.0495 7.79915 17.8012C8.94858 18.7141 9.76201 20.0632 10.284 21.7664C10.3381 21.9668 10.3122 22.1805 10.2117 22.3622C10.1113 22.5439 9.94416 22.6795 9.74564 22.7403C9.54711 22.8012 9.33272 22.7825 9.1477 22.6883C8.96268 22.594 8.82152 22.4316 8.75401 22.2352C8.29629 20.7438 7.63115 19.7101 6.80401 19.0544C6.26915 18.6292 5.55258 18.5281 4.61401 18.4046C4.18458 18.3481 3.68744 18.2821 3.23744 18.1338C2.77286 17.9812 2.29201 17.7206 1.94315 17.2338C1.32172 16.3681 1.32772 15.3481 1.48972 14.4884C1.65258 13.6304 2.00572 12.7706 2.25601 12.1355C2.34601 11.9075 2.28515 11.6786 1.97144 11.2835C1.89545 11.1895 1.81744 11.0972 1.73744 11.0066C1.66029 10.9184 1.56686 10.8095 1.48886 10.7066C1.38134 10.5709 1.29758 10.4178 1.24115 10.2541C1.19088 10.1031 1.17866 9.94201 1.20558 9.78516C1.23249 9.62831 1.29771 9.48053 1.39544 9.35493C1.53601 9.17493 1.76144 9.01893 1.93115 8.90665C2.13601 8.77036 2.40429 8.60922 2.72829 8.42665C3.51641 7.98669 4.31343 7.56289 5.11886 7.1555C5.51486 5.29808 6.35315 3.81608 7.68172 2.79008C8.39315 2.2415 9.21944 1.84122 10.1503 1.58065C10.2506 1.53027 10.3604 1.50164 10.4726 1.49665C11.3057 1.29608 12.2177 1.19922 13.2 1.19922V1.20008ZM20.5449 4.5875C20.5518 4.56723 20.5649 4.54964 20.5824 4.53719C20.5998 4.52473 20.6207 4.51804 20.6422 4.51804C20.6636 4.51804 20.6845 4.52473 20.7019 4.53719C20.7194 4.54964 20.7325 4.56723 20.7394 4.5875L21.4989 6.92836L23.8406 7.68779C23.8612 7.69455 23.8791 7.70764 23.8918 7.72519C23.9045 7.74273 23.9113 7.76384 23.9113 7.7855C23.9113 7.80717 23.9045 7.82827 23.8918 7.84582C23.8791 7.86337 23.8612 7.87646 23.8406 7.88322L21.4989 8.64265L20.7394 10.9844C20.7325 11.0046 20.7194 11.0222 20.7019 11.0347C20.6845 11.0471 20.6636 11.0538 20.6422 11.0538C20.6207 11.0538 20.5998 11.0471 20.5824 11.0347C20.5649 11.0222 20.5518 11.0046 20.5449 10.9844L19.7854 8.64265L17.4437 7.88322C17.4231 7.87646 17.4052 7.86337 17.3925 7.84582C17.3798 7.82827 17.373 7.80717 17.373 7.7855C17.373 7.76384 17.3798 7.74273 17.3925 7.72519C17.4052 7.70764 17.4231 7.69455 17.4437 7.68779L19.7854 6.92836L20.5449 4.5875Z"
          fill="white"
        />
      </svg>
    ),
  },
  {
    id: 'voice',
    title: 'Voice Clone',
    description:
      'Clone realistic voices to deliver consistent narration, explainers, and multilingual content at scale',
    href: voiceCloneURL,
    style: {
      background: 'linear-gradient(135deg, #E12AFB 0%, #F6339A 100%)',
    },
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M11 7.60107L5.00597 15.7911C4.86493 15.9837 4.79748 16.2205 4.81582 16.4586C4.83416 16.6966 4.93708 16.9203 5.10597 17.0891L5.92297 17.9071C6.09432 18.0782 6.32196 18.1814 6.56362 18.1974C6.80529 18.2134 7.04455 18.1412 7.23697 17.9941L15.09 12.0001"
          stroke="white"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M16.5 21.174C15.5 20.5 14.372 20 13 20C10.942 20 9.07204 22.356 7.00004 22C4.92804 21.644 4.22504 18.631 5.50004 17.5"
          stroke="white"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M16 12C18.7614 12 21 9.76142 21 7C21 4.23858 18.7614 2 16 2C13.2386 2 11 4.23858 11 7C11 9.76142 13.2386 12 16 12Z"
          stroke="white"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    ),
  },
  {
    id: 'image-enhance',
    title: 'Image Enhance',
    description:
      'Enhance image quality, sharpness, and detail automatically so low-quality assets look ready for production',
    href: imageEnhanceURL,
    style: {
      background: 'linear-gradient(135deg, #00BC7D 0%, #00BBA7 100%)',
    },
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M12.4198 18.9999V5.36279H14.4773V11.875H14.6438L20.3637 5.36279H22.9539L17.4538 11.5021L22.9739 18.9999H20.4968L16.0888 12.9071L14.4773 14.7583V18.9999H12.4198Z"
          fill="white"
        />
        <path
          d="M0.204712 16.3364V14.6717L6.09769 5.36279H7.40945V7.8132H6.57711L2.36214 14.4852V14.5918H10.4392V16.3364H0.204712ZM6.67034 18.9999V15.8303L6.68365 15.0712V5.36279H8.63466V18.9999H6.67034Z"
          fill="white"
        />
      </svg>
    ),
  },
  {
    id: 'video-editor',
    title: 'AI Video Editor',
    description:
      'Edit faster with AI-powered cutting, trimming, pacing, and scene optimization for ready-to-publish videos',
    href: aiVideoEditURL,
    style: {
      background: 'linear-gradient(135deg, #8E51FF 0%, #AD46FF 100%)',
    },
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M2 5C2 4.44771 2.44771 4 3 4H17C17.5523 4 18 4.44771 18 5V9.5L22 6.5V18L18 15V19C18 19.5523 17.5523 20 17 20H3C2.44771 20 2 19.5523 2 19V5Z"
          stroke="white"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M8.5 13C9.88071 13 11 11.8807 11 10.5C11 9.11929 9.88071 8 8.5 8C7.11929 8 6 9.11929 6 10.5C6 11.8807 7.11929 13 8.5 13Z"
          stroke="white"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    ),
  },
];

export const TemplatesSection = () => {
  const { templates } = useSharedTemplateList();
  const display = templates.slice(0, HOME_TEMPLATE_GRID_PAGE_SIZE);

  return (
    <RevealSection className={styles.templates}>
      <div className={styles.sectionHeader}>
        <h2>
          <em>create in seconds</em>
          <span>Start with a template,</span>
        </h2>
        <Link href="/ai-viral-template">
          See all templates
          <ArrowIcon />
        </Link>
      </div>
      <div className={styles.templateGrid}>
        {display.length > 0
          ? display.map((item) => <TemplateCard key={item.id} item={item} />)
          : Array.from({ length: HOME_TEMPLATE_GRID_PAGE_SIZE }).map((_, index) => (
              <div
                key={`template-loading-${index}`}
                className={styles.templateCardSkeleton}
              />
            ))}
      </div>
      <Link href="/ai-viral-template" className={styles.templatesCta}>
        See all templates
        <ArrowIcon />
      </Link>
    </RevealSection>
  );
};

export const ToolsTicker = () => (
  <RevealSection className={styles.toolsTicker}>
    <h2>Powerful AI Tools</h2>
    <div className={styles.tagMask}>
      <div className={styles.tagTrack}>
        {[...toolTags, ...toolTags].map((tag, index) => (
          <Link key={`${tag.label}-${index}`} href={tag.href}>
            {tag.label}
          </Link>
        ))}
      </div>
    </div>
  </RevealSection>
);

export const StepsSection = () => (
  <RevealSection className={styles.steps}>
    <h2>
      Create Viral Videos in <em>3 Simple Steps</em>
    </h2>
    <p className={styles.stepsSubtitle}>
      Pick a template, upload your content, and let AI turn it into a ready-to-share video
      in minutes.
    </p>
    <div className={styles.stepGrid}>
      {steps.map((step) => (
        <article key={step.index} className={styles.stepCard}>
          <div className={styles.stepVisual}>
            <img src={step.image} alt="" loading="lazy" decoding="async" />
          </div>
          <p className={styles.stepIndex}>{step.index}</p>
          <div className={styles.stepIcon}>{step.icon}</div>
          <h3>{step.title}</h3>
          <p>{step.description}</p>
        </article>
      ))}
    </div>
    <p className={styles.statsTitle}>Trusted by 2M+ creators and brands worldwide</p>
    <div className={styles.statsRow}>
      {stats.map((stat) => (
        <div key={stat.label}>
          <strong>{stat.value}</strong>
          <span>{stat.label}</span>
        </div>
      ))}
    </div>
  </RevealSection>
);

export const FeatureBlocks = () => (
  <RevealSection className={styles.featureBlocks}>
    {featureBlocks.map((block, index) => (
      <article key={block.title} className={index % 2 ? styles.featureReverse : ''}>
        <div className={styles.featureText}>
          <span>{block.eyebrow}</span>
          <h2>{block.title}</h2>
          <p>{block.description}</p>
          <div className={styles.featureItem}>
            {block.items?.map((li, index) => {
              return (
                <div key={index}>
                  <span className={styles.featureItemIcon}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                    >
                      <path
                        d="M10 3L4.5 8.5L2 6"
                        stroke="white"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </span>
                  <span>{li}</span>
                </div>
              );
            })}
          </div>
          <Link href={block.href}>
            {block.cta}
            <ArrowIcon />
          </Link>
        </div>
        {/* <div className={styles.featureVisual}> */}
        <img
          className={styles.featureVisual}
          src={block.image}
          alt={block.title}
          loading="lazy"
          decoding="async"
        />
        {/* </div> */}
      </article>
    ))}
  </RevealSection>
);

export const AvatarShowcase = () => {
  const [unmutedSlotKey, setUnmutedSlotKey] = useState<string | null>(null);

  return (
    <RevealSection className={styles.avatarShowcase}>
      <h2>
        <span>AI Avatar</span>
        <br />
        That Speak, Showcase, and Sell
      </h2>
      <p>
        Make studio‑quality avatar videos that deliver your message, highlight your
        products, and keep your brand consistent across every campaign. Ideal for product
        demos, ad creatives, and how‑to explainers that benefit from a dependable,
        always‑ready digital presenter.
      </p>
      <div className={styles.avatarPanels}>
        <div>
          <span>Create Your Own Avatar</span>
          <p>
            Transform any photo into an expressive brand representative for
            high-performing ads and sales funnels.
          </p>
          <div className={styles.panelMedia}>
            {AVATAR_SHOWCASE_LEFT_VIDEOS.map((item, index) => {
              const slotKey = `avatar-panel-left-${index}`;
              return (
                <HeroMarqueeVideoCard
                  key={item.id}
                  item={item}
                  slotKey={slotKey}
                  isUnmuted={unmutedSlotKey === slotKey}
                  onToggleSound={() => {
                    setUnmutedSlotKey((prev) => (prev === slotKey ? null : slotKey));
                  }}
                  compact
                  eagerVideo
                />
              );
            })}
          </div>
        </div>
        <div>
          <span>Showcase Products with Avatar</span>
          <p>
            Turn static items into authentic, UGC-style videos featuring avatars that
            showcase your products in action.
          </p>
          <div className={styles.panelMedia}>
            {AVATAR_SHOWCASE_RIGHT_VIDEOS.map((item, index) => {
              const slotKey = `avatar-panel-right-${index}`;
              return (
                <HeroMarqueeVideoCard
                  key={item.id}
                  item={item}
                  slotKey={slotKey}
                  isUnmuted={unmutedSlotKey === slotKey}
                  onToggleSound={() => {
                    setUnmutedSlotKey((prev) => (prev === slotKey ? null : slotKey));
                  }}
                  compact
                  eagerVideo
                />
              );
            })}
          </div>
        </div>
      </div>
      <Link href={HOME_CTA_URL} className={styles.primaryCta}>
        Create Product Avatar Video
        <ArrowIcon />
      </Link>
    </RevealSection>
  );
};

export const ToolCardsSection = () => (
  <RevealSection className={styles.toolCards} id="features">
    <h2>
      Powerful AI Tools, <em>All in One Place</em>
    </h2>
    <p className={styles.toolCardsDesc}>
      From translation and cleanup to face swaps, voice cloning, and enhancement, create
      polished videos faster with one powerful AI toolkit.
    </p>
    <div className={styles.toolGrid}>
      {toolCards.map((tool) => (
        <Link href={tool.href} key={tool.id} className={styles.toolCard}>
          <div
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: tool.style.background,
            }}
          >
            {tool.icon}
          </div>
          <div className={styles.toolCardContent}>
            <h3>{tool.title}</h3>
            <p>{tool.description}</p>
            <small>
              Learn more
              <ArrowIcon color={'#C4B4FF'} />
            </small>
          </div>
        </Link>
      ))}
    </div>
  </RevealSection>
);

export const TestimonialsSection = () => (
  <RevealSection className={styles.testimonials}>
    <h2>
      Loved by <em>creators worldwide</em>
    </h2>
    <div className={styles.testimonialGrid}>
      {testimonials.map((item) => (
        <article key={item.name}>
          <div className={styles.stars}>★★★★★</div>
          <p>&quot;{item.quote}&quot;</p>
          <div className={styles.testimonialUser}>
            <Image src={item.icon} alt={item.name} width={40} height={40} />
            <div className={styles.testimonialUserInfo}>
              <strong>{item.name}</strong>
              <span>{item.role}</span>
            </div>
          </div>
        </article>
      ))}
    </div>
  </RevealSection>
);

export const FAQSection = () => {
  const [active, setActive] = useState<number | null>(0);

  return (
    <RevealSection className={styles.faq}>
      <h2>
        Frequently asked <em>questions</em>
      </h2>
      <p className={styles.faqSubtitle}>
        Everything you need to know about creating AI videos
      </p>
      <div className={styles.faqList}>
        {FAQ_LIST.slice(0, 8).map((item, index) => (
          <article key={item.question} className={active === index ? styles.openFaq : ''}>
            <button
              type="button"
              onClick={() => setActive(active === index ? null : index)}
            >
              {item.question}
              <span>{active === index ? '−' : '+'}</span>
            </button>
            <div>
              <p>{parseFaqInlineMarkdownLinks(item.answer || item.listIntro || '')}</p>
            </div>
          </article>
        ))}
      </div>
    </RevealSection>
  );
};
